// Lectura de los movimientos de una cuenta de préstamo: saldo inicial,
// pagado, y reparto de cada letra en interés y capital. Funciones puras;
// importes en céntimos. En la cuenta del préstamo (deuda negativa) las letras
// entran como importes positivos y los intereses devengados como negativos.

import { PAYEE_INTERESES } from '@actual-app/core/shared/prestamos';
import type { DatosPrestamo } from '@actual-app/core/shared/prestamos';

export type MovimientoDeuda = {
  id: string;
  /** `YYYY-MM-DD`. */
  date: string;
  amount: number;
  payee: string | null;
  /** Nota del movimiento (para reconocer los intereses que apunta la app). */
  notes?: string | null;
  /** Movimiento de apertura de la cuenta (`starting_balance_flag`). */
  apertura?: boolean;
  /** ¿Es un traspaso (con otra cuenta, del presupuesto o no)? */
  traspaso?: boolean;
};

const RE_SALDO_INICIAL = /starting balance|saldo (inicial|de apertura)/i;

export function esInteres(m: MovimientoDeuda): boolean {
  return m.payee === PAYEE_INTERESES;
}

/** Letra (pago) = entrada positiva que no es un ajuste de interés. */
export function esPago(m: MovimientoDeuda): boolean {
  return m.amount > 0 && !esInteres(m);
}

function ordenados(movs: readonly MovimientoDeuda[]): MovimientoDeuda[] {
  return [...movs].sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Deuda inicial (positiva). Orden: campo `saldo_inicial` de la nota; el
 * movimiento «Starting Balance»; el máximo de deuda de todo el historial.
 */
export function saldoInicial(
  movs: readonly MovimientoDeuda[],
  datos: Pick<DatosPrestamo, 'saldo_inicial'> | null,
): number {
  if (datos?.saldo_inicial && datos.saldo_inicial > 0) {
    return Math.round(datos.saldo_inicial * 100);
  }
  const orden = ordenados(movs);
  const apertura = orden.find(
    m =>
      m.amount < 0 &&
      (m.apertura || (m.payee && RE_SALDO_INICIAL.test(m.payee))),
  );
  if (apertura) {
    // El saldo inicial es el de cierre del día de apertura: así un ajuste
    // del mismo día (p. ej. un «Starting Balance» mal tecleado en YNAB y su
    // «Balance Adjustment») no infla la deuda inicial.
    const mismoDia = orden
      .filter(m => m.date === apertura.date)
      .reduce((suma, m) => suma + m.amount, 0);
    if (mismoDia < 0) {
      return -mismoDia;
    }
    return -apertura.amount;
  }
  let acumulado = 0;
  let maxDeuda = 0;
  for (const m of orden) {
    acumulado += m.amount;
    maxDeuda = Math.max(maxDeuda, -acumulado);
  }
  return maxDeuda;
}

/** Suma de letras del mes `YYYY-MM`. */
export function pagadoEnMes(
  movs: readonly MovimientoDeuda[],
  mes: string,
): number {
  return movs
    .filter(m => esPago(m) && m.date.startsWith(mes))
    .reduce((s, m) => s + m.amount, 0);
}

export type ReparteLetra = { interes: number; capital: number };

/**
 * Reparte cada letra en interés y capital. Si el mes de la letra tiene un
 * movimiento de «Intereses del préstamo», ese es el interés; si no, se
 * calcula sobre la deuda justo antes de la letra (saldo actual deshaciendo
 * los movimientos desde esa letra) con interés anual / 12.
 */
export function repartirLetras(
  movs: readonly MovimientoDeuda[],
  saldoActual: number,
  interesAnual: number,
): Map<string, ReparteLetra> {
  const orden = ordenados(movs);
  const resultado = new Map<string, ReparteLetra>();
  // Saldo de la cuenta justo antes de cada movimiento (de atrás hacia delante).
  let saldoPosterior = saldoActual;
  for (let i = orden.length - 1; i >= 0; i--) {
    const m = orden[i];
    const saldoAntes = saldoPosterior - m.amount;
    saldoPosterior = saldoAntes;
    if (!esPago(m)) {
      continue;
    }
    const mes = m.date.slice(0, 7);
    const registrado = orden
      .filter(x => esInteres(x) && x.date.startsWith(mes))
      .reduce((s, x) => s - x.amount, 0);
    const deudaAntes = Math.max(0, -saldoAntes);
    const calculado = Math.round((deudaAntes * interesAnual) / 100 / 12);
    const interes = Math.min(m.amount, Math.max(0, registrado || calculado));
    resultado.set(m.id, { interes, capital: m.amount - interes });
  }
  return resultado;
}

function interesMensual(deuda: number, interesAnual: number): number {
  return Math.round((Math.max(0, deuda) * interesAnual) / 100 / 12);
}

/**
 * Saldo de la cuenta justo antes de `fecha`: los movimientos de días
 * anteriores y, del mismo día, los que no son letras ni intereses (p. ej. la
 * apertura). Se saltan los ids de `excluir`.
 */
function saldoAntesDe(
  movs: readonly MovimientoDeuda[],
  fecha: string,
  excluir: ReadonlySet<string> = new Set(),
): number {
  let saldo = 0;
  for (const m of movs) {
    if (excluir.has(m.id)) {
      continue;
    }
    if (m.date < fecha || (m.date === fecha && !esPago(m) && !esInteres(m))) {
      saldo += m.amount;
    }
  }
  return saldo;
}

function primeraLetraDelMes(
  movs: readonly MovimientoDeuda[],
  mes: string,
): MovimientoDeuda | null {
  return ordenados(movs).find(m => esPago(m) && m.date.startsWith(mes)) ?? null;
}

/**
 * Interés del mes `YYYY-MM` (positivo): deuda JUSTO ANTES de la primera letra
 * del mes × interés nominal / 12, redondeado al céntimo. Sin letra en el mes,
 * sobre la deuda al final de los movimientos.
 */
export function interesDelMesARegistrar(
  movs: readonly MovimientoDeuda[],
  mes: string,
  interesAnual: number,
): number {
  const letra = primeraLetraDelMes(movs, mes);
  const saldo = letra
    ? saldoAntesDe(movs, letra.date)
    : movs.reduce((s, m) => s + m.amount, 0);
  return interesMensual(-saldo, interesAnual);
}

export function hayInteresEnMes(
  movs: readonly MovimientoDeuda[],
  mes: string,
): boolean {
  return movs.some(m => esInteres(m) && m.date.startsWith(mes));
}

export type InteresNuevo = {
  /** `YYYY-MM`. */
  mes: string;
  /** Fecha de la letra del mes (`YYYY-MM-DD`). */
  fecha: string;
  /** Interés en céntimos, positivo (el movimiento va en negativo). */
  importe: number;
};

/**
 * Letra para los intereses automáticos: traspaso ENTRANTE desde otra cuenta,
 * del presupuesto o no (la cuota se paga desde la que elija la persona,
 * Vadik 27-oct-2026). Ajustes de saldo, saldo inicial o devoluciones no son
 * letra aquí (el reparto «Letras: interés y capital» de la ficha sigue usando
 * `esPago`).
 */
export function esLetraAutomatica(m: MovimientoDeuda): boolean {
  return m.amount > 0 && !!m.traspaso && !esInteres(m);
}

/**
 * ¿El mes ya tiene interés? El de la app («Intereses del préstamo») o
 * CUALQUIER salida que no sea traspaso ni apertura (p. ej. intereses
 * importados de YNAB con otro beneficiario): así nunca se cuenta dos veces.
 */
export function mesConInteres(
  movs: readonly MovimientoDeuda[],
  mes: string,
): boolean {
  return movs.some(
    m =>
      m.date.startsWith(mes) &&
      (esInteres(m) || (m.amount < 0 && !m.traspaso && !m.apertura)),
  );
}

/**
 * Intereses que faltan: un movimiento por cada mes que tiene una letra
 * (`esLetraAutomatica`) y ningún interés (`mesConInteres`), con la deuda
 * justo antes de la letra × TIN / 12, fechado el día de la letra. Desde
 * `desde` (`YYYY-MM-DD`) si se da, y nunca letras posteriores a `hoy`. Los
 * intereses nuevos de un mes cuentan para la deuda de los siguientes. Cuáles
 * se apuntan solos y cuáles piden confirmación: `repartirPendientes`.
 */
export function interesesAutomaticosPendientes(
  movs: readonly MovimientoDeuda[],
  interesAnual: number | null,
  hoy: string,
  desde?: string | null,
): InteresNuevo[] {
  if (interesAnual == null || interesAnual <= 0) {
    return [];
  }
  const orden = ordenados(movs);
  const meses = new Set<string>();
  for (const m of orden) {
    const mes = m.date.slice(0, 7);
    if (
      !esLetraAutomatica(m) ||
      m.date > hoy ||
      (desde && mes < desde.slice(0, 7))
    ) {
      continue;
    }
    meses.add(mes);
  }
  const trabajo: MovimientoDeuda[] = [...orden];
  const nuevos: InteresNuevo[] = [];
  for (const mes of [...meses].sort()) {
    const letra =
      ordenados(trabajo).find(
        m => esLetraAutomatica(m) && m.date.startsWith(mes),
      ) ?? null;
    if (!letra || mesConInteres(trabajo, mes)) {
      continue;
    }
    const importe = interesMensual(
      -saldoAntesDe(trabajo, letra.date),
      interesAnual,
    );
    if (importe <= 0) {
      continue;
    }
    nuevos.push({ mes, fecha: letra.date, importe });
    trabajo.push({
      id: `nuevo-${mes}`,
      date: letra.date,
      amount: -importe,
      payee: PAYEE_INTERESES,
    });
  }
  return nuevos;
}

/** Primer mes (`YYYY-MM`) que se apunta solo: el anterior al de `hoy`. */
export function primerMesAutomatico(hoy: string): string {
  const [a, m] = hoy.slice(0, 7).split('-').map(Number);
  return m === 1 ? `${a - 1}-12` : `${a}-${String(m - 1).padStart(2, '0')}`;
}

/**
 * Los intereses pendientes del mes en curso y del anterior se apuntan solos;
 * los de meses más antiguos solo con confirmación (`anteriores`). Los
 * automáticos se calculan sin los anteriores (que aún no existen).
 */
export function repartirPendientes(
  movs: readonly MovimientoDeuda[],
  interesAnual: number | null,
  hoy: string,
  desde?: string | null,
): { automaticos: InteresNuevo[]; anteriores: InteresNuevo[] } {
  const primero = primerMesAutomatico(hoy);
  const desdeAuto =
    desde && desde.slice(0, 7) > primero ? desde : `${primero}-01`;
  const automaticos = interesesAutomaticosPendientes(
    movs,
    interesAnual,
    hoy,
    desdeAuto,
  );
  const anteriores = interesesAutomaticosPendientes(
    movs,
    interesAnual,
    hoy,
    desde,
  ).filter(p => p.mes < primero);
  return { automaticos, anteriores };
}

/**
 * ¿Es un interés apuntado por la app? Beneficiario «Intereses del préstamo»
 * y nota con el formato de la app («Intereses de septiembre de 2026»), en
 * cualquiera de los textos dados (`notasApp` = nota esperada por mes).
 */
export function esInteresDeLaApp(
  m: MovimientoDeuda,
  notaApp: (mes: string) => readonly string[],
): boolean {
  if (!esInteres(m) || !m.notes) {
    return false;
  }
  const nota = m.notes.trim();
  return notaApp(m.date.slice(0, 7)).some(n => n === nota);
}

export type CambioInteres = {
  id: string;
  /** `YYYY-MM`. */
  mes: string;
  /** Importes en céntimos, positivos. */
  antes: number;
  despues: number;
};

/**
 * Recalcula con `interesAnual` los intereses apuntados por la app (`ids`):
 * para cada uno, deuda justo antes de la primera letra de su mes (o de su
 * propia fecha si el mes no tiene letra) × TIN / 12, en orden y contando los
 * intereses ya recalculados. Devuelve solo los que cambian.
 */
export function recalcularIntereses(
  movs: readonly MovimientoDeuda[],
  ids: ReadonlySet<string>,
  interesAnual: number,
): CambioInteres[] {
  const trabajo = ordenados(movs).map(m => ({ ...m }));
  const cambios: CambioInteres[] = [];
  for (const m of trabajo) {
    if (!ids.has(m.id)) {
      continue;
    }
    const mes = m.date.slice(0, 7);
    const delMes = new Set(
      trabajo
        .filter(x => esInteres(x) && x.date.startsWith(mes))
        .map(x => x.id),
    );
    const letra = primeraLetraDelMes(trabajo, mes);
    const fecha = letra ? letra.date : m.date;
    const despues = interesMensual(
      -saldoAntesDe(trabajo, fecha, delMes),
      interesAnual,
    );
    const antes = -m.amount;
    m.amount = -despues;
    if (despues !== antes) {
      cambios.push({ id: m.id, mes, antes, despues });
    }
  }
  return cambios;
}
